import { ArrowLeft } from "lucide-react"
import { Suspense } from "react"
import { useNavigate, useParams } from "react-router"
import { toast } from "sonner"
import { propertyApi } from "@/api/client/property"
import { roomApi } from "@/api/client/room"
import { getErrorMessage } from "@/api/instance"
import { RoomForm, type RoomFormValues } from "@/components/room"
import { ErrorPage } from "@/components/ui/error"
import { ReactErrorBoundary } from "@/components/ui/error-boundary"
import { Spinner } from "@/components/ui/spinner/Spinner"

const RoomAddPageContent = ({ propertyId }: { propertyId: string }) => {
  const navigate = useNavigate()

  // roomCreateのmutationを取得
  const { mutateAsync: createRoom, isPending } = roomApi.useRoomCreate({
    mutation: {
      onError: (error) =>
        toast.error("部屋の登録に失敗しました", { description: getErrorMessage(error) }),
    },
  })

  const { data: propertyData } = propertyApi.usePropertyGetSuspense(propertyId, {
    withOwner: "true",
  })
  if (propertyData.property.owner?.echoesOwnerId) {
    return (
      <ErrorPage
        title="エラー"
        message="Echoes連携物件の部屋は追加できません。"
        backPath={`/properties/${propertyId}`}
      />
    )
  }

  const handleSubmit = async (values: RoomFormValues) => {
    try {
      await createRoom({
        data: {
          propertyId: Number(propertyId),
          name: values.name,
          layout: values.layout,
          // 面積・階数は任意。未入力は API が受け付ける 0 で送る（オーナー画面と同じ）
          size: values.size ?? 0,
          floor: values.floor ?? 0,
        },
      })

      toast.success("部屋を登録しました")
      navigate(`/properties/${propertyId}`, { state: { refetch: true } })
    } catch {
      // 失敗の表示は mutation の onError が担当する
    }
  }

  const handleCancel = () => {
    navigate(`/properties/${propertyId}`)
  }

  return (
    <ReactErrorBoundary>
      <div>
        <div className="flex items-center gap-2 p-6">
          <button
            type="button"
            onClick={() => navigate(`/properties/${propertyId}`)}
            className="flex items-center justify-center w-7 h-7 bg-white rounded-[5px] shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]"
          >
            <ArrowLeft size={14} className="text-[#354267]" />
          </button>
          <span className="text-sm font-semibold text-[#3f4652]">物件詳細に戻る</span>
        </div>

        <div className="max-w-[895px] mx-auto px-6 pb-8">
          <RoomForm onSubmit={handleSubmit} onCancel={handleCancel} isSubmitting={isPending} />
        </div>
      </div>
    </ReactErrorBoundary>
  )
}

// 直接 URL ロード時は ProtectedRoute の同期更新中に suspend するため、
// Suspense 境界が無いと React が throw して白画面になる(RoomEditPage と同じ構成)。
export const RoomAddPage = () => {
  const { propertyId } = useParams<{ propertyId: string }>()

  if (!propertyId || Number.isNaN(Number(propertyId))) {
    return (
      <ErrorPage
        title="エラー"
        message="物件IDが指定されていません。URLを確認してください。"
        backPath="/properties"
      />
    )
  }

  return (
    <ReactErrorBoundary>
      <Suspense
        fallback={
          <div className="p-10 flex flex-col items-center justify-center">
            <Spinner className="text-slate-600 size-10" />
          </div>
        }
      >
        <RoomAddPageContent propertyId={propertyId} />
      </Suspense>
    </ReactErrorBoundary>
  )
}
